import { type Analytics, getAnalytics } from 'firebase/analytics';
import { type FirebaseOptions, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { type FirebasePerformance, getPerformance } from 'firebase/performance';
import type { RemoteConfig } from 'firebase/remote-config';
import { getStorage } from 'firebase/storage';
import i18next from 'i18next';
import { z } from 'zod';

import {
  DigitalInformationSchema,
  SocialMediaAccountsSchema,
} from '@/features/chat/types/message';

const {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_MEASUREMENT_ID,
} = import.meta.env;

const firebaseConfig: FirebaseOptions = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
  measurementId: VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const firestore = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

auth.languageCode = i18next.language;

const shouldInitializePerformance =
  import.meta.env.VITE_APP_ENV === 'production' ||
  import.meta.env.VITE_APP_ENV === 'development';
const isPerformanceMonitoringSupported =
  typeof window !== 'undefined' &&
  'fetch' in window &&
  'PerformanceObserver' in window &&
  'indexedDB' in window;

let performanceMonitoringInstance: FirebasePerformance | undefined;

if (shouldInitializePerformance && isPerformanceMonitoringSupported) {
  try {
    performanceMonitoringInstance = getPerformance(firebaseApp);
  } catch {
    performanceMonitoringInstance = undefined;
  }
}

export const performanceMonitoring = performanceMonitoringInstance;

let remoteConfigPromise: Promise<RemoteConfig> | null = null;

export let analytics: Analytics;

if (typeof window !== 'undefined') {
  analytics = getAnalytics(firebaseApp);
}

export async function getRemoteConfigInstance(): Promise<RemoteConfig> {
  remoteConfigPromise ??= import('firebase/remote-config').then(
    ({ getRemoteConfig }) => {
      const remoteConfig = getRemoteConfig(firebaseApp);
      remoteConfig.defaultConfig = {
        webPeopleSearched: JSON.stringify(
          DEFAULT_REMOTE_CONFIG.webPeopleSearched,
        ),
        webPeopleSearchedInside: JSON.stringify(
          DEFAULT_REMOTE_CONFIG.webPeopleSearchedInside,
        ),
        webRelatedQuestions: JSON.stringify(
          DEFAULT_REMOTE_CONFIG.webRelatedQuestions,
        ),
        pricing: JSON.stringify(DEFAULT_REMOTE_CONFIG.pricing),
        webUpsell: JSON.stringify({}),
        subscriptionCancelDelayMinutes: JSON.stringify(
          DEFAULT_REMOTE_CONFIG.subscriptionCancelDelayMinutes,
        ),
      } satisfies Partial<Record<keyof RemoteConfigType, string>>;
      remoteConfig.settings.minimumFetchIntervalMillis =
        import.meta.env.VITE_VERCEL_ENV === 'production'
          ? 1000 * 60 * 60 * 12
          : 1;

      return remoteConfig;
    },
  );

  return await remoteConfigPromise;
}

const RemoteConfigPopularPersonSchema = z.object({
  name: z.string(),
  thumbnail: z.string(),
  image_url_list: z.array(z.string()),
  search_result: z.string(),
  url_list: z.array(
    z.object({
      title: z.string(),
      link: z.string(),
    }),
  ),
  related_questions: z.array(z.string()),
  social_media_list: SocialMediaAccountsSchema.optional(),
  digital_information: DigitalInformationSchema.optional(),
});

export const RemoteConfigPeopleSearchedSchema = z.object({
  title: z.string(),
  mostPopularPersons: z.array(RemoteConfigPopularPersonSchema),
});

export const RemoteConfigRelatedQuestionsSchema = z.object({
  related_questions: z.array(z.string()),
});

const RemoteConfigPricingProSchema = z.object({
  price: z.string(),
  pricePerDay: z.string(),
  savePercent: z.string().optional(),
  popular: z.boolean().optional(),
  specialOffer: z.boolean().optional(),
  introPrice: z.string().optional(),
});

const RemoteConfigPricingCycleSchema = z.object({
  pro: RemoteConfigPricingProSchema,
});

const getOptionalUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (trimmedValue === '') {
    return undefined;
  }

  try {
    const parsedUrl = new URL(trimmedValue);
    return parsedUrl.href === '' ? undefined : trimmedValue;
  } catch {
    return undefined;
  }
};

const OptionalRemoteConfigUrlSchema = z.preprocess(
  getOptionalUrl,
  z.string().url().optional(),
);

export const RemoteConfigWebUpsellSchema = z.preprocess(
  (value) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return value;
    }

    return undefined;
  },
  z
    .object({
      amount: z.unknown().optional(),
      currency: z.unknown().optional(),
      originalAmount: z.unknown().optional(),
      priceId: z.unknown().optional(),
      readUrl: z.unknown().optional(),
      downloadUrl: z.unknown().optional(),
    })
    .optional(),
);

const ConfiguredRemoteConfigWebUpsellSchema = z.object({
  amount: z.coerce.number().positive(),
  currency: z.string().trim().min(1),
  originalAmount: z.coerce.number().positive(),
  priceId: z.string().trim().min(1),
  readUrl: OptionalRemoteConfigUrlSchema,
  downloadUrl: OptionalRemoteConfigUrlSchema,
});

const ConfiguredRemoteConfigWebUpsellLibrarySchema = z.object({
  readUrl: OptionalRemoteConfigUrlSchema,
  downloadUrl: OptionalRemoteConfigUrlSchema,
});

export const RemoteConfigPricingSchema = z.object({
  weekly: RemoteConfigPricingCycleSchema.optional(),
  monthly: RemoteConfigPricingCycleSchema.optional(),
  quarterly: RemoteConfigPricingCycleSchema.optional(),
  yearly: RemoteConfigPricingCycleSchema.optional(),
});

export const RemoteConfigSubscriptionCancelDelayMinutesSchema = z.coerce
  .number()
  .int()
  .nonnegative();

export type RemoteConfigPricing = z.infer<typeof RemoteConfigPricingSchema>;
export type RemoteConfigPricingPro = z.infer<
  typeof RemoteConfigPricingProSchema
>;
export type RemoteConfigWebUpsell = z.infer<typeof RemoteConfigWebUpsellSchema>;
export type ConfiguredRemoteConfigWebUpsell = z.infer<
  typeof ConfiguredRemoteConfigWebUpsellSchema
>;
export type ConfiguredRemoteConfigWebUpsellLibrary = z.infer<
  typeof ConfiguredRemoteConfigWebUpsellLibrarySchema
>;

export const RemoteConfigSchema = z.object({
  webPeopleSearched: RemoteConfigPeopleSearchedSchema,
  webPeopleSearchedInside: RemoteConfigPeopleSearchedSchema,
  webRelatedQuestions: RemoteConfigRelatedQuestionsSchema,
  pricing: RemoteConfigPricingSchema,
  webUpsell: RemoteConfigWebUpsellSchema,
  subscriptionCancelDelayMinutes:
    RemoteConfigSubscriptionCancelDelayMinutesSchema,
});

export type RemoteConfigType = z.infer<typeof RemoteConfigSchema>;

export const DEFAULT_REMOTE_CONFIG: RemoteConfigType = {
  webPeopleSearched: {
    title: '',
    mostPopularPersons: [],
  },
  webPeopleSearchedInside: {
    title: '',
    mostPopularPersons: [],
  },
  webRelatedQuestions: {
    related_questions: [],
  },
  pricing: {},
  webUpsell: undefined,
  subscriptionCancelDelayMinutes: 0,
};

export function getConfiguredWebUpsellConfig(
  config: RemoteConfigType | null,
): ConfiguredRemoteConfigWebUpsell | null {
  const parsedWebUpsell = ConfiguredRemoteConfigWebUpsellSchema.safeParse(
    config?.webUpsell,
  );

  return parsedWebUpsell.success ? parsedWebUpsell.data : null;
}

export function getConfiguredWebUpsellLibraryConfig(
  config: RemoteConfigType | null,
): ConfiguredRemoteConfigWebUpsellLibrary | null {
  const parsedWebUpsell =
    ConfiguredRemoteConfigWebUpsellLibrarySchema.safeParse(config?.webUpsell);

  return parsedWebUpsell.success ? parsedWebUpsell.data : null;
}
